import React, { createContext, startTransition, useCallback, useContext, useEffect, useMemo, useRef, useState, useDeferredValue } from 'react'
import { calculateAnalysis } from '../lib/analysis-utils'
import { mapChainRowToOptionData } from '../lib/data-utils'
// tRPC REMOVED — all data fetching uses direct API endpoints now
import { useMarketStore } from '../services/store'
import { useSettingsStore } from '../stores/settingsStore'
import { usePrecomputedStore } from '../stores/precomputeStore'
import type { AnalysisMode, AnalysisResult } from '../types'
import type { MarketAnalysisResponse } from '@options-oracle/types'
import { getFirebaseAuth } from '../services/firebase'
import { useQuery } from '@tanstack/react-query'

import { extractSnapshotOptions, timeToMin, extractTimeMinutes } from '../lib/snapshot-search'
import { deriveMarketMetrics } from '../lib/analytics-engine'
import AnalyticsWorker from '../workers/analytics.worker.ts?worker'
import { marketDataBus } from '../services/MarketDataBus'
import { useIndexIndicators } from './useIndexIndicators'
import ky from 'ky'

// FREEZE FIX: Frozen singletons prevent referential instability.
// New `[]` on every access would trigger selector re-renders.
const EMPTY_ARRAY: any[] = Object.freeze([]) as unknown as any[]
const EMPTY_FROZEN: readonly any[] = Object.freeze([])

// GLOBAL CACHE to prevent 17 components from recalculating the exact same analysis simultaneously
const GLOBAL_DASHBOARD_CACHE: Record<string, { fingerprint: string; data: any }> = {}

// GLOBAL throttler for store updates. Because useDashboardData is used in 9+ components,
// an internal useRef allows all 9 instances to push identical updates to the store simultaneously.
let globalLastStoreWrite = 0

// PERF DIAGNOSTIC: Track how many hook instances exist and which components own them
let __hookInstanceCounter = 0
const __activeInstances = new Map<number, string>()

// ═══════════════════════════════════════════════════════════════════════════
// FREEZE FIX v3: CONTEXT PROVIDER PATTERN
// Previously, useDashboardData was instantiated 15 times across the app.
// Each instance ran: 12 Zustand selectors + DataBus subscription + 4 useMemo
// chains + 1 useQuery. 15 × that = 939ms initial mount violation.
//
// Now: The computation runs ONCE in DashboardDataProvider (mounted in _auth.tsx).
// All 15 consumers read the cached result via React Context — zero overhead.
// ═══════════════════════════════════════════════════════════════════════════

// The return type of useDashboardData
type DashboardDataResult = {
    marketData: any
    analysis: any
    expiries: any[]
    isLoading: boolean
    effectiveExpiry: string
    filteredOptions: any[]
    baseOptions: any[]
    baseSpot: number
    baseTimestamp: string
    maxValues: any
    mode: any
}

const EMPTY_DASHBOARD_RESULT: DashboardDataResult = {
    marketData: { symbol: '', spotPrice: 0, dayChange: 0, dayChangeP: 0, vix: 0, vixChange: 0, vixChangeP: 0, options: [], timestamp: 0, expiry: '', isLive: true, atmStrike: 0 },
    analysis: null,
    expiries: [],
    isLoading: true,
    effectiveExpiry: '',
    filteredOptions: [],
    baseOptions: [],
    baseSpot: 0,
    baseTimestamp: '',
    maxValues: { ceOI: 0, peOI: 0, ceVol: 0, peVol: 0, ceOIChange: 0, peOIChange: 0, ceChangeP: 0, peChangeP: 0, maxIV: 0, maxCombinedOI: 0, maxCombinedVol: 0 },
    mode: 'OI',
}

const DashboardDataContext = createContext<DashboardDataResult>(EMPTY_DASHBOARD_RESULT)

/**
 * useDashboardData — The PUBLIC hook that all 15 consumers call.
 * Reads from React Context (zero computation, just a context lookup).
 */
export const useDashboardData = (): DashboardDataResult => {
    return useContext(DashboardDataContext)
}

/**
 * DashboardDataProvider — Mount this ONCE in the _auth.tsx layout.
 * All heavy computation happens here: Zustand subscriptions, DataBus reads,
 * useMemo chains, and useQuery. The result is shared via Context.
 */
export function DashboardDataProvider({ children }: { children: React.ReactNode }) {
    const data = _useDashboardDataInternal()
    return React.createElement(DashboardDataContext.Provider, { value: data }, children)
}

const _useDashboardDataInternal = () =>
{
    // Stable instance ID (for optional diagnostics via window.__PERF_DIAG = true)
    const __instanceId = useRef<number>(-1)
    if (__instanceId.current === -1) {
        __instanceId.current = ++__hookInstanceCounter
    }
    const __iid = __instanceId.current
    // FREEZE FIX: Granular selectors — subscribes ONLY to fields actually used.
    // Previously `useSettingsStore()` subscribed to ALL 30+ fields, causing cascade
    // re-renders on every unrelated change (theme, subTab, inspectionSource, etc.)
    const settingsSymbol = useSettingsStore( s => s.symbol )
    const settingsExpiry = useSettingsStore( s => s.expiry )
    const settingsMode = useSettingsStore( s => s.mode )
    const settingsRange = useSettingsStore( s => s.range )
    const settingsBaseTime = useSettingsStore( s => s.baseTime )
    const settingsIsLive = useSettingsStore( s => s.isLive )
    const settingsHistoricalDate = useSettingsStore( s => s.historicalDate )
    const settingsExecutionMode = useSettingsStore( s => s.executionMode )
    const settingsSimulationTime = useSettingsStore( s => s.simulationTime )

    // Prioritize store settings (no overrides in Context pattern)
    const activeSymbol = settingsSymbol || ''
    const rawExpiry = settingsExpiry
    const activeMode = settingsMode

    const { indexIndicators } = useIndexIndicators(activeSymbol, settingsHistoricalDate)

    // Optimized Granular Selectors to prevent unnecessary re-renders
    const _rawChain = useMarketStore( ( state ) => state.data[ activeSymbol ] || EMPTY_ARRAY )
    const _spotPrice = useMarketStore( ( state ) => state.indexLtp[ activeSymbol ] || 0 )
    
    // 🚀 PERF FIX: Removed useDeferredValue on raw data streams.
    // Deferring the primary 2-second live data stream was causing upstream render starvation,
    // where React would continuously abort the calculation of fresh data and the UI would freeze.
    // Phase 1 is already optimized to be synchronous & lightweight (< 2ms), so we don't need deferral.
    const rawChain = _rawChain
    const spotPrice = _spotPrice

    // FREEZE FIX: Subscribe to INDIVIDUAL meta fields instead of the whole meta object.
    // Previously `state.marketMeta?.[activeSymbol]` returned a NEW object reference every 2s tick
    // (because GlobalMarketEngine sets timestamp: Date.now()), causing ALL 8 hook instances to
    // recompute their heavy memoizedData useMemo simultaneously, freezing the main thread.
    // PERF FIX: These meta values are display-only (shown in header cards).
    // They change every 2s poll but do NOT affect any computation.
    // Reading them non-reactively prevents the rawResult useMemo from
    // re-running its entire Phase 1 + Phase 2 cascade (1054ms) on every tick.
    // Components that need live meta updates can subscribe directly via useMarketStore.
    // Re-added reactive primitive selectors: Zustand will only re-render when the primitive value changes,
    // so the 2s timestamp tick in the parent object will NOT trigger a re-render here.
    const metaDayChange = useMarketStore( s => s.marketMeta?.[ activeSymbol ]?.dayChange || 0 )
    const metaDayChangeP = useMarketStore( s => s.marketMeta?.[ activeSymbol ]?.dayChangeP || 0 )
    const metaVix = useMarketStore( s => s.marketMeta?.[ activeSymbol ]?.vix || 0 )
    const metaVixChange = useMarketStore( s => s.marketMeta?.[ activeSymbol ]?.vixChange || 0 )
    const metaVixChangeP = useMarketStore( s => s.marketMeta?.[ activeSymbol ]?.vixChangeP || s.marketMeta?.[ activeSymbol ]?.vixChangePercent || 0 )
    // NEW: Get metrics from store
    // PERF FIX: Read marketMetrics imperatively (non-reactive) — the per-symbol
    // metrics object is replaced wholesale on every tick, so a reactive selector
    // would force this hook to re-render every 2s. marketMetrics is only consumed
    // inside the computedAnalysis memo (dependency: analysisFingerprint), which
    // does NOT re-run on metrics changes. Reading via getState() avoids the spurious
    // re-render while still serving the latest value when the memo does recompute.
    const marketMetrics = useMarketStore.getState().marketMetrics?.[ activeSymbol ]
    const expiryList = useMarketStore( ( state ) => state.expiryMap[ activeSymbol ] || EMPTY_ARRAY )
    const inspectionSource = useSettingsStore( s => s.inspectionSource )
    // ═══════════════════════════════════════════════════════════════════════════
    // FREEZE FIX v4 CRITICAL: IMPERATIVE BASE RESOLUTION
    //
    // We cannot subscribe to the global history array here because it updates every
    // minute, which would invalidate the DashboardDataContext and force massive
    // tables (OptionChain, Ranking) to re-render synchronously (200ms+ freeze).
    //
    // Instead, we subscribe imperatively and ONLY trigger a React update if the
    // actual 09:16 (or sim) snapshot changes (usually happens just once per load).
    // ═══════════════════════════════════════════════════════════════════════════
    
    // 🚀 FREEZE FIX: Subscribe to history LENGTH (primitive), NOT the array reference.
    // Previously: `useMarketStore(s => s.intradayHistory?.[sym])` returned a new array
    // reference on EVERY store update (even unrelated ones like indexLtp changes),
    // because Zustand's default equality check is `===` and the nested lookup creates
    // a new reference path. This caused the snapshot useEffect below to re-run on
    // every 2s tick → setBaseSnapshotTick++ → Provider re-render → ALL consumers re-render.
    // NOW: We subscribe to the LENGTH only (primitive, stable between ticks).
    // The actual array is read imperatively via getState() in the useEffect.
    const historyLength = useMarketStore( ( state ) => {
        if ( inspectionSource === 'HistoricalSnapshot' ) return state.historicalHistory?.[ activeSymbol ]?.length ?? 0
        if ( inspectionSource === 'RangeIntraday' || inspectionSource === 'RangeEOD' ) return state.rangeHistory?.[ activeSymbol ]?.length ?? 0
        return state.intradayHistory?.[ activeSymbol ]?.length ?? 0
    } )

    const [baseSnapshotTick, setBaseSnapshotTick] = useState(0)
    const baseSnapshotRef = useRef<any>(null)
    const simSnapshotRef = useRef<any>(null)

    useEffect(() => {
        const resolveSnapshots = () => {
            // Read the actual array imperatively (no reactive subscription)
            const storeState = useMarketStore.getState()
            let h: any[]
            if ( inspectionSource === 'HistoricalSnapshot' ) {
                h = storeState.historicalHistory?.[ activeSymbol ] || EMPTY_ARRAY
            } else if ( inspectionSource === 'RangeIntraday' || inspectionSource === 'RangeEOD' ) {
                h = storeState.rangeHistory?.[ activeSymbol ] || EMPTY_ARRAY
            } else {
                h = storeState.intradayHistory?.[ activeSymbol ] || EMPTY_ARRAY
            }
            if (!h || h.length === 0) return

            // Resolve Base Snapshot
            const targetMinBase = timeToMin(useSettingsStore.getState().baseTime || '09:16')
            let bestDiffBase = Infinity
            let newBase: any = null
            for (const snap of h) {
                const snapMin = extractTimeMinutes(snap)
                if (isNaN(snapMin)) continue
                const diff = Math.abs(targetMinBase - snapMin)
                if (diff === 0) { newBase = snap; break }
                if (diff < bestDiffBase) { bestDiffBase = diff; newBase = snap }
            }

            // Resolve Sim Snapshot
            const targetMinSim = timeToMin(useSettingsStore.getState().simulationTime || '15:30')
            let bestDiffSim = Infinity
            let newSim: any = null
            if (inspectionSource === 'HistoricalSnapshot') {
                for (const snap of h) {
                    const snapMin = extractTimeMinutes(snap)
                    if (isNaN(snapMin)) continue
                    const diff = Math.abs(targetMinSim - snapMin)
                    if (diff === 0) { newSim = snap; break }
                    if (diff < bestDiffSim) { bestDiffSim = diff; newSim = snap }
                }
            }

            let changed = false
            if (newBase !== baseSnapshotRef.current) {
                baseSnapshotRef.current = newBase
                changed = true
            }
            if (newSim !== simSnapshotRef.current) {
                simSnapshotRef.current = newSim
                changed = true
            }

            if (changed) {
                setBaseSnapshotTick(t => t + 1)
            }
        }

        // Only re-run when the history LENGTH changes, symbol/source changes,
        // or the user changes the time slicer (simulationTime/baseTime).
        resolveSnapshots()
    }, [historyLength, activeSymbol, inspectionSource, settingsSimulationTime, settingsBaseTime])

    const lastBackupTime = useRef<number>( 0 )
    const symbolExpiries = useMarketStore( ( state ) => state.expiryMap[ activeSymbol ] || EMPTY_ARRAY )
    const effectiveExpiry: string = rawExpiry || ( symbolExpiries.length > 0 ? ( symbolExpiries[ 0 ] as string ) : '' )

    const timelines = usePrecomputedStore((state: any) => state.timelines)
    const currentIsLive = useSettingsStore(state => state.isLive)
    const settingsBaseTimePattern = useSettingsStore(state => state.baseTime)
    const aotCacheKey = `${activeSymbol}_${effectiveExpiry}_${settingsRange}_${activeMode}_${settingsBaseTimePattern}_${historyLength}`
    const precomputedTimeline = timelines[aotCacheKey]
    
    const workerIsLoading = useMarketStore( ( state ) => state.data[ activeSymbol ] === undefined )
    const lastHistoryUpdateTime = useRef<number>( 0 )

    const precomputedFrame = useMemo(() => {
        if (currentIsLive || !precomputedTimeline || inspectionSource !== 'HistoricalSnapshot') return null
        const targetTs = typeof settingsSimulationTime === 'string' ? (new Date(settingsSimulationTime).getTime() || 0) : (settingsSimulationTime || 0)
        return precomputedTimeline.find((f: any) => f.timestamp === targetTs) || null
    }, [currentIsLive, precomputedTimeline, inspectionSource, settingsSimulationTime])

    // 1. Transform V4 (OptionChainRow[]) -> V2 (MarketData / OptionData[])
    // const options = rawChain.map((row) => mapChainRowToOptionData(row, activeSymbol, effectiveExpiry, Date.now()))
    // Optimization: Memoize options transformation
    // actually it is better to do it in the second useMemo to avoid double loop if not needed?
    // But we need options for local calc fallback.

    // 2. Resolve Base Options (Critical for Variance Calculation)
    // ALWAYS load base options from history if available (for variance calculations)
    // --- OPTIMIZATION: SPLIT MEMOIZATION ---
    // Part 1: Base Data (Updates every 1 minute)
    // We calculate this separately so it doesn't re-run on every live tick (1s)
    const { baseOptions, baseSpot, baseTimestamp, foundBaseTime } = useMemo( () =>
    {
        let bOpts: any[] = []
        let bSpot = 0
        let bTimestamp = ''
        const baseSnapshot = baseSnapshotRef.current

        if ( baseSnapshot )
        {
            bTimestamp = baseSnapshot.timestampIST || baseSnapshot.timestamp || ''
            if ( baseSnapshot.optionsData || baseSnapshot.optionChain || baseSnapshot.strategyChainData || baseSnapshot.options )
            {
                bOpts = extractSnapshotOptions( baseSnapshot, activeSymbol, effectiveExpiry )
            }

            if ( baseSnapshot.indexData && baseSnapshot.indexData[ activeSymbol ] )
            {
                bSpot = baseSnapshot.indexData[ activeSymbol ].lastPrice || 0
            }

            // Fallbacks to extract spot if indexData is missing or empty
            if ( !bSpot && baseSnapshot.spotPrice ) bSpot = baseSnapshot.spotPrice
            if ( !bSpot && baseSnapshot.indexLtp ) bSpot = baseSnapshot.indexLtp
            // If we still don't have it, but we have options, grab middle strike (heuristic) or 0
            if ( !bSpot && bOpts.length > 0 )
            {
                bSpot = bOpts[ Math.floor( bOpts.length / 2 ) ]?.strike || bOpts[ 0 ]?.strike || 0
            }
        }
        return { baseOptions: bOpts, baseSpot: bSpot, baseTimestamp: bTimestamp || settingsBaseTime, foundBaseTime: !!baseSnapshot }
    }, [ baseSnapshotTick, activeSymbol, settingsBaseTime, effectiveExpiry ] )

    // --- Explicit Turso Fetch Removed ---
    // User Request: "if GlobalHistory is empty without any issue live data should be render"
    // Fetching remotely here caused main thread freezes because it fell back to parsing massive
    // CSV files synchronously. We now strictly rely on GlobalHistoryFetcher (Web Worker) for this.

    // Combine Local vs Remote Base Options — MEMOIZED to prevent new array references on every render
    const { finalBaseOptions, finalBaseSpot, finalBaseTimestamp } = useMemo( () => {
        let fOpts = baseOptions
        let fSpot = baseSpot
        let fTs = baseTimestamp

        return { finalBaseOptions: fOpts, finalBaseSpot: fSpot, finalBaseTimestamp: fTs }
    }, [ baseOptions, baseSpot, baseTimestamp ] )

    // --- OPTIMIZATION: SPLIT MEMOIZATION ---
    // Part 1.5: Simulation Data (For Historical Playback)
    const { finalSimOptions, finalSimSpot, finalSimTimestamp } = useMemo( () =>
    {
        let sOpts: any[] = []
        let sSpot = 0
        let sTimestamp = ''
        const simSnapshot = simSnapshotRef.current

        if ( simSnapshot && inspectionSource === 'HistoricalSnapshot' )
        {
            sTimestamp = simSnapshot.timestampIST || simSnapshot.timestamp || ''
            if ( simSnapshot.optionsData || simSnapshot.optionChain || simSnapshot.strategyChainData || simSnapshot.options )
            {
                sOpts = extractSnapshotOptions( simSnapshot, activeSymbol, effectiveExpiry )
            }

            if ( simSnapshot.indexData && simSnapshot.indexData[ activeSymbol ] )
            {
                sSpot = simSnapshot.indexData[ activeSymbol ].lastPrice || 0
            }

            if ( !sSpot && simSnapshot.spotPrice ) sSpot = simSnapshot.spotPrice
            if ( !sSpot && simSnapshot.indexLtp ) sSpot = simSnapshot.indexLtp
            if ( !sSpot && sOpts.length > 0 )
            {
                sSpot = sOpts[ Math.floor( sOpts.length / 2 ) ]?.strike || sOpts[ 0 ]?.strike || 0
            }
        }
        return { finalSimOptions: sOpts, finalSimSpot: sSpot, finalSimTimestamp: sTimestamp || settingsSimulationTime }
    }, [ baseSnapshotTick, activeSymbol, settingsSimulationTime, effectiveExpiry, inspectionSource ] )

    // Part 2: Live Data & Analysis
    // ═══════════════════════════════════════════════════════════════════════════
    // PERMANENT FREEZE FIX v7: TWO-PHASE ASYNC COMPUTATION
    //
    // BEFORE: useMemo ran deriveMarketMetrics() synchronously during render.
    //   This took 1281ms on first data arrival, completely freezing the UI.
    //
    // AFTER: 
    //   Phase 1 (useMemo, ~1ms): Returns raw chain data immediately.
    //     → The table renders numbers instantly, no analysis highlights.
    //   Phase 2 (useEffect + requestIdleCallback, ~200ms during idle):
    //     → Computes heavy analytics (S/R, variance, PCR) in browser idle time.
    //     → Updates state → context → children re-render with highlights.
    //   Result: UI NEVER freezes. User sees data instantly, analysis appears 
    //   ~200ms later without any jank.
    // ═══════════════════════════════════════════════════════════════════════════

    // Phase 1: IMMEDIATE raw data (synchronous, <2ms)
    const rawResult = useMemo(() => {
        const hasLiveChain = rawChain.length > 0
        const hasHistoricalOptions = finalSimOptions.length > 0 && inspectionSource === 'HistoricalSnapshot'

        if (!hasLiveChain && !hasHistoricalOptions) {
            return {
                marketData: {
                    symbol: activeSymbol,
                    spotPrice: spotPrice || 0,
                    dayChange: metaDayChange,
                    dayChangeP: metaDayChangeP,
                    vix: metaVix,
                    vixChange: metaVixChange,
                    vixChangeP: metaVixChangeP,
                    options: EMPTY_ARRAY,
                    timestamp: 0,
                    expiry: effectiveExpiry,
                    isLive: true,
                    atmStrike: 0,
                },
                analysis: null,
                expiries: expiryList,
                isLoading: !!activeSymbol,
                effectiveExpiry,
                filteredOptions: EMPTY_ARRAY,
                baseOptions: finalBaseOptions,
                baseSpot: finalBaseSpot,
                baseTimestamp: finalBaseTimestamp,
                maxValues: {
                    ceOI: 0, peOI: 0, ceVol: 0, peVol: 0,
                    ceOIChange: 0, peOIChange: 0,
                    ceChangeP: 0, peChangeP: 0, maxIV: 0,
                    maxCombinedOI: 0, maxCombinedVol: 0,
                },
                mode: activeMode,
                _phase: 'empty' as const,
            }
        }

        // Read timestamp non-reactively
        const metaSnapshot = useMarketStore.getState().marketMeta?.[activeSymbol]
        const rawTs = metaSnapshot?.timestamp
        const currentTimestamp = typeof rawTs === 'string' ? (new Date(rawTs).getTime() || 0) : (rawTs || 0)

        let liveSpot = spotPrice
        const currentIsLive = useSettingsStore.getState().isLive

        // Historical override
        let resolvedOptions: any[] = EMPTY_ARRAY as any[]
        if (inspectionSource === 'HistoricalSnapshot' && finalSimOptions.length > 0) {
            liveSpot = finalSimSpot
            resolvedOptions = finalSimOptions
        } else if (hasLiveChain) {
            // Lightweight transform: just map fields, NO heavy analysis
            resolvedOptions = rawChain.map(row => mapChainRowToOptionData(row, activeSymbol, effectiveExpiry, currentTimestamp))
        }

        // Lightweight ATM calculation
        let atmStrike = 0
        if (resolvedOptions.length >= 2 && liveSpot > 0) {
            const diffs: Record<number, number> = {}
            for (let i = 1; i < Math.min(20, resolvedOptions.length); i++) {
                const d = resolvedOptions[i]!.strike - resolvedOptions[i - 1]!.strike
                if (d > 0) diffs[d] = (diffs[d] || 0) + 1
            }
            const commonStep = Object.keys(diffs).reduce((a, b) => ((diffs[parseInt(a, 10)] || 0) > (diffs[parseInt(b, 10)] || 0) ? a : b), '50')
            const step = parseInt(commonStep, 10) || 50
            atmStrike = Math.round(liveSpot / step) * step
        }

        let effectiveDayChange = metaDayChange
        let effectiveDayChangeP = metaDayChangeP

        if ((!effectiveDayChange || effectiveDayChange === 0 || inspectionSource === 'HistoricalSnapshot') && indexIndicators?.prevDay?.close && indexIndicators.prevDay.close > 0 && liveSpot > 0) {
            effectiveDayChange = liveSpot - indexIndicators.prevDay.close
            effectiveDayChangeP = (effectiveDayChange / indexIndicators.prevDay.close) * 100
        }

        return {
            marketData: {
                symbol: activeSymbol,
                spotPrice: liveSpot,
                dayChange: effectiveDayChange,
                dayChangeP: effectiveDayChangeP,
                vix: metaVix,
                vixChange: metaVixChange,
                vixChangeP: metaVixChangeP,
                options: resolvedOptions,
                timestamp: currentTimestamp,
                expiry: effectiveExpiry,
                isLive: currentIsLive ?? true,
                atmStrike,
            },
            analysis: null as any, // Will be filled by Phase 2
            expiries: expiryList,
            isLoading: false,
            effectiveExpiry,
            filteredOptions: (() => {
                // FREEZE FIX v8: Slice the options to the exact range BEFORE Phase 1 render.
                // Prevents returning all 130 strikes when spotPrice is 0 (during load),
                // which previously caused a 500ms+ synchronous render freeze.
                if (resolvedOptions.length === 0) return resolvedOptions;
                
                let anchorStrike = atmStrike;
                if (!anchorStrike && resolvedOptions.length > 0) {
                    // Fallback to the middle strike if spot price isn't loaded yet
                    anchorStrike = resolvedOptions[Math.floor(resolvedOptions.length / 2)].strike;
                }
                
                let atmObj = resolvedOptions.find((d) => d.strike === anchorStrike);
                if (!atmObj) {
                    atmObj = resolvedOptions.reduce((prev, curr) =>
                        Math.abs(curr.strike - anchorStrike) < Math.abs(prev.strike - anchorStrike) ? curr : prev
                    );
                }
                
                const finalAnchor = atmObj?.strike || anchorStrike;
                
                const belowATM = resolvedOptions
                    .filter((d) => d.strike < finalAnchor)
                    .sort((a, b) => b.strike - a.strike)
                    .slice(0, settingsRange);
                    
                const aboveATM = resolvedOptions
                    .filter((d) => d.strike > finalAnchor)
                    .sort((a, b) => a.strike - b.strike)
                    .slice(0, settingsRange);
                    
                return [...belowATM.reverse(), ...(atmObj ? [atmObj] : []), ...aboveATM];
            })(),
            baseOptions: finalBaseOptions,
            baseSpot: finalBaseSpot,
            baseTimestamp: finalBaseTimestamp,
            maxValues: {
                ceOI: 0, peOI: 0, ceVol: 0, peVol: 0,
                ceOIChange: 0, peOIChange: 0,
                ceChangeP: 0, peChangeP: 0, maxIV: 0,
                maxCombinedOI: 0, maxCombinedVol: 0,
                maxCallOI: 0, maxPutOI: 0, maxCallVol: 0, maxPutVol: 0,
                maxOI: 0, maxVol: 0, maxCombinedOIChange: 0, maxCombinedVolChange: 0,
                maxCombinedOIChangeP: 0, maxCombinedVolChangeP: 0,
            },
            mode: activeMode,
            _phase: 'raw' as const,
        }
    }, [
        rawChain, spotPrice,
        metaDayChange, metaDayChangeP, metaVix, metaVixChange, metaVixChangeP,
        effectiveExpiry, expiryList,
        finalBaseOptions, finalBaseSpot, finalBaseTimestamp,
        finalSimOptions, finalSimSpot, finalSimTimestamp,
        inspectionSource, activeSymbol, activeMode,
        indexIndicators?.prevDay?.close,
    ])

    // Phase 2: DEFERRED heavy analysis (async, runs during idle time)
    const [computedAnalysis, setComputedAnalysis] = useState<{
        analysis: any
        filteredOptions: any[]
        maxValues: any
        atmStrike: number
    } | null>(null)

    // FREEZE FIX v9: Round spotPrice in the fingerprint to the nearest strike step.
    // Without rounding, spotPrice changes every 2s live tick (e.g., 24850.10 → 24850.25),
    // causing the Phase 2 useEffect to fire and requestIdleCallback to force-run
    // deriveMarketMetrics() synchronously (via the 500ms timeout).
    // With rounding, analysis only recomputes when spot moves a full strike step (50/100 pts).
    const strikeStep = activeSymbol === 'NIFTY' ? 50 : 100
    const roundedSpot = spotPrice > 0 ? Math.round(spotPrice / strikeStep) * strikeStep : 0

    // Track the fingerprint to know when to recompute
    const analysisFingerprint = `${activeSymbol}|${effectiveExpiry}|${activeMode}|${settingsRange}|${roundedSpot}|${rawResult.marketData.options.length}|${finalBaseOptions.length}|${settingsBaseTime}|${inspectionSource}|${settingsSimulationTime}`

    useEffect(() => {
        // Nothing to compute
        if (rawResult._phase === 'empty') {
            setComputedAnalysis(null)
            return
        }
        // PERF: Skip heavy analysis if data hasn't loaded yet.
        // Prevents wasted CPU during the loading window before Zustand hydration.
        if (rawChain.length === 0) {
            return
        }

        // AOT BYPASS: If we already have the precomputed frame for this simulation time,
        // we DO NOT need to run Phase 2 analysis! It will just waste CPU and cause UI stutter.
        if (precomputedFrame) {
            return
        }

        let cancelled = false
        let worker: Worker | null = null;
        let idleHandle: number | undefined
        let timeoutHandle: ReturnType<typeof setTimeout> | undefined

        const __t0 = performance.now()

        worker = new AnalyticsWorker()
        worker.onmessage = (event) => {
            if (cancelled) return
            
            if (event.data.type === 'SUCCESS') {
                const derived = event.data.payload
                let resolvedOptions = derived.options
                const filteredOptions = derived.analysis?.tableRows || resolvedOptions

                // Attach analysis to marketData for downstream consumers
                if (derived.analysis) {
                    (rawResult.marketData as any).analysis = derived.analysis
                }

                if (typeof window !== 'undefined' && (window as any).__PERF_DIAG) {
                    console.log(`[PERF-DIAG] Phase2 Analysis computed via Worker in ${(performance.now() - __t0).toFixed(1)}ms`)
                }

                startTransition(() => {
                    setComputedAnalysis({
                        analysis: derived.analysis,
                        filteredOptions,
                        maxValues: derived.maxValues,
                        atmStrike: derived.atmStrike,
                    })
                })
            } else {
                console.error('[Dashboard] Analytics Worker Error:', event.data.error)
            }
        }

        worker.postMessage({
            symbol: activeSymbol,
            options: rawResult.marketData.options,
            spotPrice: rawResult.marketData.spotPrice,
            baseOptions: finalBaseOptions,
            baseSpot: finalBaseSpot,
            effectiveExpiry,
            activeMode,
            settingsRange,
            currentTimestamp: rawResult.marketData.timestamp,
            hasExplicitBaseTime: !!settingsBaseTime,
            workerMetrics: settingsExecutionMode === 'BETA' ? marketMetrics : undefined
        })

        return () => {
            cancelled = true
            if (worker) worker.terminate()
        }
    }, [analysisFingerprint]) // eslint-disable-line react-hooks/exhaustive-deps

    // Merge Phase 1 + Phase 2
    // CRITICAL FIX: Always use Phase 1's filteredOptions (fresh every 2s tick).
    // Phase 2's filteredOptions was STALE — it only updated when analysisFingerprint
    // changed (spot moves a full strike step), causing OptionChainTable to freeze.
    // Phase 2 now only contributes: analysis highlights + maxValues.
    const memoizedData = useMemo(() => {
        if (!computedAnalysis) return rawResult

        const merged = {
            ...rawResult,
            analysis: computedAnalysis.analysis,
            // USE Phase 1's fresh filteredOptions — NOT Phase 2's stale snapshot.
            // Phase 1 runs on every 2s tick with latest OI/LTP/Volume from Zustand.
            // Phase 2 only re-runs when spot moves a full strike step (50/100 pts).
            filteredOptions: rawResult.filteredOptions,
            maxValues: computedAnalysis.maxValues,
        }

        // Update marketData.atmStrike from computed analysis
        if (computedAnalysis.atmStrike) {
            merged.marketData = { ...rawResult.marketData, atmStrike: computedAnalysis.atmStrike }
        }

        if (computedAnalysis.analysis) {
            (merged.marketData as any).analysis = computedAnalysis.analysis
        }

        const isLoading = (!merged.marketData || merged.filteredOptions.length === 0) && !!activeSymbol

        return { ...merged, isLoading }
    }, [rawResult, computedAnalysis, activeSymbol])

    // We can safely return memoizedData directly because heavy consumers like OptionChainTable
    // and DashboardHistoryTable use React.useDeferredValue internally to prevent main thread blocking.

    // State to track if we've waited too long for initial data
    const [ isTimedOut, setIsTimedOut ] = useState( false )

    useEffect( () =>
    {
        let timer: NodeJS.Timeout
        if (
            ( !memoizedData.marketData || memoizedData.filteredOptions.length === 0 ) &&
            !!activeSymbol &&
            memoizedData.isLoading
        )
        {
            timer = setTimeout( () =>
            {
                setIsTimedOut( true )
            }, 8000 ) // 8 seconds timeout
        } else if ( memoizedData.marketData && memoizedData.filteredOptions.length > 0 )
        {
            setIsTimedOut( false )
        }
        return () => clearTimeout( timer )
    }, [ memoizedData.marketData, memoizedData.filteredOptions.length, memoizedData.isLoading, activeSymbol ] )

    // BACKUP LOGIC MOVED TO GlobalMarketEngine.tsx

    return isTimedOut ? { ...memoizedData, isLoading: false } : memoizedData
}
